import { useState, useEffect, useCallback } from 'react';
import { PreLoadedDoc } from '@/lib/templates/types';

const STORAGE_KEY = 'link_solo_daily_queue';

export interface SoloQueuePatient {
  id: string;
  patient_name: string;
  patient_dob: string | null;
  patient_sex: string | null;
  appointment_time: string; // ISO string
  planned_procedure: string;
  facility_name: string | null;
  payer_name: string | null;
  pre_loaded_docs: PreLoadedDoc[];
  added_by: string;
  added_at: string;
}

export interface SoloDailyQueue {
  date: string; // YYYY-MM-DD
  surgeon_name: string;
  patients: SoloQueuePatient[];
}

function getTodayKey(): string {
  return new Date().toISOString().split('T')[0];
}

function loadQueue(): SoloDailyQueue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as SoloDailyQueue;
      // If stored queue is from a different day, start fresh
      if (parsed.date === getTodayKey()) {
        return parsed;
      }
    }
  } catch {
    // Corrupt data — reset
  }
  return {
    date: getTodayKey(),
    surgeon_name: 'Dr. John J. Larkin',
    patients: [],
  };
}

function saveQueue(queue: SoloDailyQueue): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

export function useSoloDailyQueue() {
  const [queue, setQueue] = useState<SoloDailyQueue>(loadQueue);

  // Sync to localStorage on every change
  useEffect(() => {
    saveQueue(queue);
  }, [queue]);

  const addPatient = useCallback((patient: Omit<SoloQueuePatient, 'id' | 'added_at'>) => {
    setQueue(prev => ({
      ...prev,
      patients: [
        ...prev.patients,
        {
          ...patient,
          id: `solo-q-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          added_at: new Date().toISOString(),
        },
      ],
    }));
  }, []);

  const addDocsToPatient = useCallback((patientId: string, docs: PreLoadedDoc[]) => {
    setQueue(prev => ({
      ...prev,
      patients: prev.patients.map(p =>
        p.id === patientId
          ? { ...p, pre_loaded_docs: [...p.pre_loaded_docs, ...docs] }
          : p
      ),
    }));
  }, []);

  const removePatient = useCallback((patientId: string) => {
    setQueue(prev => ({
      ...prev,
      patients: prev.patients.filter(p => p.id !== patientId),
    }));
  }, []);

  const clearQueue = useCallback(() => {
    setQueue({
      date: getTodayKey(),
      surgeon_name: 'Dr. John J. Larkin',
      patients: [],
    });
  }, []);

  // Sort patients chronologically by appointment time
  const sortedPatients = [...queue.patients].sort(
    (a, b) => new Date(a.appointment_time).getTime() - new Date(b.appointment_time).getTime()
  );

  return {
    queue,
    sortedPatients,
    addPatient,
    addDocsToPatient,
    removePatient,
    clearQueue,
    patientCount: queue.patients.length,
  };
}
